'use client'
import { resolveIconForRender } from '@systhemaui/payload/fields/iconPicker/resolveIconForRender'

import { Section, Card, Heading, Stack, Button, ButtonTitle, ButtonIcon } from '@systhemaui/react'
import type { Page } from '@/payload-types'
import { ButtonVariant } from '@systhemaui/core/tmp/types'

import type { SectionProps } from '@systhemaui/next'
import type { ReactNode } from 'react'

import TrailerButton from '@/components/TrailerButton'

export interface CallToActionButton {
  title?: string
  variant?: ButtonVariant
  iconBefore?: string
  iconAfter?: string
  linkType?: 'custom' | 'internal'
  url?: string
  reference?: Page | number | null
  newTab?: boolean
}

export interface CallToActionData {
  title: string
  content: ReactNode
  buttons: CallToActionButton[]
}

type Props = Omit<SectionProps, 'content'> & {
  data: CallToActionData
}

const CallToAction = ({ data, ...props }: Props) => {
  const { theme = 'default', layoutBackground = 'base', ...rest } = props
  return (
    <Section
      padding={3}
      theme={theme}
      layoutBackground={layoutBackground}
      className="before:from-burgundy-700/30 before:to-burgundy-700/0 relative before:absolute before:inset-0 before:z-10 before:bg-linear-to-t"
      {...rest}
    >
      <Card
        variant="highlighted"
        className="before:from-burgundy-700/30 before:to-burgundy-700/0 relative z-20 px-8 py-12 before:absolute before:inset-0 before:-z-10 before:bg-linear-to-r md:px-12 md:py-16"
      >
        <Heading.h2 className="text-center">{data.title}</Heading.h2>
        <div className="text-center">{data.content}</div>
        <Stack alignItems="center" justifyContent="center" gap="sm" wrap>
          <TrailerButton />
          {data.buttons.map((button, index) => {
            const href =
              button.linkType === 'custom'
                ? button.url || ''
                : button.reference &&
                    typeof button.reference === 'object' &&
                    'fullPath' in button.reference
                  ? (button.reference as Page).fullPath || '/'
                  : ''

            return (
              <Button.a
                key={button.title || index}
                href={href}
                variant={button.variant}
                target={button.newTab ? '_blank' : undefined}
                rel={button.newTab ? 'noopener noreferrer' : undefined}
              >
                {button.iconBefore && (
                  <ButtonIcon
                    dangerouslySetInnerHTML={{
                      __html: resolveIconForRender(button.iconBefore).svg,
                    }}
                  />
                )}
                {button.title && <ButtonTitle>{button.title}</ButtonTitle>}
                {button.iconAfter && (
                  <ButtonIcon
                    dangerouslySetInnerHTML={{ __html: resolveIconForRender(button.iconAfter).svg }}
                  />
                )}
              </Button.a>
            )
          })}
        </Stack>
      </Card>
    </Section>
  )
}

export default CallToAction
